import { Box, Button, Center, Container, Grid, Loader, Stack, Text } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import TitleComponent from "@/components/shared/TitleComponent";
import { ecosystemHeadNavState, sectorHeadsHref } from "@/pages/ecosystem-heads/ecosystemHeads.const";
import { getSectorBySlug } from "@/pages/home/sectorCatalog";
import { useSectorEcosystem } from "./hooks/useSectorEcosystem";
import { getSectorVisual } from "./sectorEcosystem.const";
import SectorEcosystemHero from "./components/SectorEcosystemHero";
import SectorOpportunitiesCard from "./components/SectorOpportunitiesCard";
import SectorCompaniesDonutCard from "./components/SectorCompaniesDonutCard";
import SectorCollaborationModelsCard from "./components/SectorCollaborationModelsCard";
import SectorEcosystemHeadsCarousel from "./components/SectorEcosystemHeadsCarousel";
import { sectorListFooterLink } from "./sectorCompaniesList.const";
import CompaniesOverview from "./components/CompaniesOverview";
import SectorPromoPopupHost from "./components/SectorPromoPopupHost";
import { PAGE_PADDING_X } from "@/shared/constants/layout.constants";
import { backButtonFilledStyles } from "@/shared/styles/backButton";

export default function SectorEcosystemPage() {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth();
  const sector = getSectorBySlug(slug);

  if (!slug || !sector || sector.comingSoon) {
    return <Navigate to={isAuthenticated ? "/my-discovery" : "/"} replace />;
  }

  const { data, loading, error, reload } = useSectorEcosystem(slug);
  const visual = getSectorVisual(slug);
  const backHref = isAuthenticated ? "/my-discovery" : "/";
  const opportunitiesFooter = sectorListFooterLink(slug, "opportunities", isAuthenticated);
  const companiesFooter = sectorListFooterLink(slug, "companies", isAuthenticated);
  const headsHref = sectorHeadsHref(slug);
  const headsNavState = ecosystemHeadNavState(`/sector/${slug}`);

  const backToDiscoverButton = (
    <Button
      component={Link}
      to={backHref}
      variant="filled"
      size="xs"
      w="fit-content"
      leftSection={<IconArrowLeft size={14} />}
      styles={backButtonFilledStyles}
    >
      Back to Discover
    </Button>
  );

  return (
    <Box
      pb={isAuthenticated ? "md" : 48}
      style={{
        backgroundColor: isAuthenticated ? "#f7f9fe" : "#F8FAFC",
        minHeight: isAuthenticated ? undefined : "100vh",
      }}
    >
      <SectorPromoPopupHost sectorSlug={slug} />

      {isAuthenticated ? (
        <TitleComponent leftSection={backToDiscoverButton} />
      ) : null}

      <Container
        fluid
        px={PAGE_PADDING_X}
        py={{ base: "md", sm: isAuthenticated ? undefined : "xl" }}
        pt={isAuthenticated ? { base: "xs", sm: "xs" } : undefined}
        pb={isAuthenticated ? { base: "md", sm: "md" } : undefined}
      >
        <Stack gap="lg">
          {!isAuthenticated ? backToDiscoverButton : null}

          {loading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : error || !data ? (
            <Center py="xl">
              <Stack align="center" gap="sm">
                <Text c="dimmed">{error ?? "Sector ecosystem not found."}</Text>
                <Button variant="light" onClick={() => void reload()}>
                  Retry
                </Button>
              </Stack>
            </Center>
          ) : (
            <Stack gap={0}>
              <SectorEcosystemHero slug={slug} overview={data} visual={visual} />

              <Box
                visibleFrom="lg"
                style={{ display: "flex", alignItems: "flex-start", width: "100%" }}
              >
                <Box
                  style={{
                    flex: "1 1 50%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <SectorOpportunitiesCard overview={data} {...opportunitiesFooter} />
                  <CompaniesOverview
                    slug={slug}
                    overview={data}
                    isAuthenticated={isAuthenticated}
                    {...companiesFooter}
                  />
                </Box>
                <Box
                  style={{
                    flex: "1 1 50%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <SectorEcosystemHeadsCarousel
                    sectorSlug={slug}
                    overview={data}
                    headsHref={headsHref}
                    headsNavState={headsNavState}
                    isAuthenticated={isAuthenticated}
                  />
                  <Box style={{ display: "flex", width: "100%" }}>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <SectorCompaniesDonutCard slug={slug} />
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <SectorCollaborationModelsCard overview={data} />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Stack gap={0} hiddenFrom="lg">
                <SectorOpportunitiesCard overview={data} {...opportunitiesFooter} />
                <SectorEcosystemHeadsCarousel
                  sectorSlug={slug}
                  overview={data}
                  headsHref={headsHref}
                  headsNavState={headsNavState}
                  isAuthenticated={isAuthenticated}
                />
                <CompaniesOverview
                  slug={slug}
                  overview={data}
                  isAuthenticated={isAuthenticated}
                  {...companiesFooter}
                />
                <Grid gutter={0}>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <SectorCompaniesDonutCard slug={slug} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <SectorCollaborationModelsCard overview={data} />
                  </Grid.Col>
                </Grid>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
